import React from "react";

import {
  Axis,
  Chart,
  Coordinate,
  Interaction,
  Interval,
  Legend,
  Tooltip,
} from "bizcharts";

import useTranslations from "../../../hooks/useTranslations";

import { getClosestGrade } from "../../../utils/reviewUtils";

type Datum = {
  gradeType: string;
  grade: number;
  user: string;
  userId: string;
  date: number;
};

type UserGradeHorizontalBarChartProps = {
  data: Datum[];
  options: { value: number; label: string }[];
  onBarClick: (datum: Datum) => void;
};

const UserGradeHorizontalBarChart: React.FC<UserGradeHorizontalBarChartProps> =
  ({ data, options, onBarClick }) => {
    const t = useTranslations();

    return (
      <Chart
        data={data}
        height={500}
        autoFit
        padding="auto"
        scale={{
          grade: {
            range: [0, 1],
            ticks: options.map((option) => option.value),
          },
        }}
      >
        <Coordinate transpose />
        <Interval
          adjust={[
            {
              type: "dodge",
              marginRatio: 0,
            },
          ]}
          tooltip={[
            "gradeType*grade",
            (name: "collaborator" | "referents", value) => ({
              value: value !== null ? getClosestGrade(options, value).label : t('reviews.ungraded'),
              name: t(`reviews.${name}` as const) as string,
            }),
          ]}
          color="gradeType"
          position="user*grade"
        />
        <Legend
          name="gradeType"
          position="bottom"
          itemName={{
            formatter: (text: "collaborator" | "referents") =>
              t(`reviews.${text}` as const),
          }}
        />
        <Tooltip shared />
        <Axis
          name="grade"
          label={{
            formatter: (value) => getClosestGrade(options, parseFloat(value)).label,
          }}
        />
        <Interaction type="active-region" />
        <Interaction
          type="element-selected"
          config={{
            start: [
              {
                trigger: "plot:click",
                action: "tooltip:show",
                callback: (context) =>
                  onBarClick && onBarClick(context?.event?.data?.data),
              },
            ],
          }}
        />
      </Chart>
    );
  };

export default React.memo(UserGradeHorizontalBarChart);
